import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { HeartHandshake, CheckCircle2, Send, Loader2, AlertTriangle } from 'lucide-react';
import { useKiooAudio } from '../AudioProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MobilePrayer = () => {
  const { currentStation } = useKiooAudio();
  const stationName = currentStation?.name || 'Kioo Radio';
  const stationSlug = currentStation?.slug || 'kioo';

  const [form, setForm] = useState({
    listener_name: '',
    location: '',
    request: '',
    phone: '',
    is_public: true,
    consent: false,
  });
  const [phase, setPhase] = useState('idle'); // idle | submitting | sent | error
  const [error, setError] = useState('');

  const canSubmit = useMemo(
    () =>
      form.listener_name.trim() &&
      form.location.trim() &&
      form.request.trim().length >= 3 &&
      form.consent &&
      phase !== 'submitting',
    [form, phase]
  );

  const submit = async () => {
    setError('');
    if (!canSubmit) {
      setError('Please fill in your Name, Location, Request and tick the consent box.');
      return;
    }
    setPhase('submitting');
    try {
      await axios.post(`${BACKEND_URL}/api/prayer-requests`, {
        listener_name: form.listener_name.trim(),
        location: form.location.trim(),
        request: form.request.trim(),
        phone: form.phone.trim() || null,
        is_public: form.is_public,
        station_slug: stationSlug,
      });
      setPhase('sent');
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        'Could not send your prayer request. Please try again.';
      setError(String(msg));
      setPhase('idle');
    }
  };

  if (phase === 'sent') {
    return (
      <div className="px-4 pt-8 pb-4">
        <div
          data-testid="mobile-prayer-sent"
          className="rounded-2xl bg-white border p-5 text-center shadow-sm"
          style={{ borderColor: 'var(--kioo-primary)' }}
        >
          <div
            className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
            style={{ background: 'var(--kioo-background)' }}
          >
            <CheckCircle2
              className="w-10 h-10"
              style={{ color: 'var(--kioo-primary)' }}
            />
          </div>
          <h2
            className="mt-4 text-xl font-extrabold"
            style={{ color: 'var(--kioo-primary-dark)' }}
          >
            We're praying with you.
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Your prayer request has been sent to {stationName}. Our presenters
            will lift it up on air and in private.
          </p>
          <button
            data-testid="mobile-prayer-send-another"
            onClick={() =>
              setForm({
                listener_name: '',
                location: '',
                request: '',
                phone: '',
                is_public: true,
                consent: false,
              }) || setPhase('idle')
            }
            className="mt-5 w-full py-3 rounded-full font-semibold text-white"
            style={{ background: 'var(--kioo-primary)' }}
          >
            Send another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-6">
      <div
        data-testid="mobile-prayer-banner"
        className="relative overflow-hidden rounded-2xl px-4 py-4 text-white shadow-md"
        style={{
          background:
            'linear-gradient(135deg, var(--kioo-primary-dark) 0%, var(--kioo-primary) 55%, var(--kioo-accent) 100%)',
        }}
      >
        {currentStation?.logo_url && (
          <img
            src={currentStation.logo_url}
            alt=""
            aria-hidden="true"
            className="pointer-events-none select-none absolute inset-0 m-auto h-[130%] w-auto object-contain opacity-20"
            style={{ mixBlendMode: 'screen' }}
          />
        )}
        <div className="relative z-10 flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.2)' }}
          >
            <HeartHandshake className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold leading-tight">
              Prayer Wall
            </h1>
            <p className="text-xs text-white/90 mt-1">
              Send a request to <b>{stationName}</b> — a presenter will read
              it and pray with you on air.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div
          data-testid="mobile-prayer-error"
          className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div
        data-testid="mobile-prayer-form"
        className="mt-4 rounded-2xl bg-white border border-gray-100 p-5 shadow-sm space-y-3"
      >
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">
            Your Name *
          </span>
          <input
            data-testid="mobile-prayer-name"
            type="text"
            value={form.listener_name}
            onChange={(e) =>
              setForm({ ...form, listener_name: e.target.value })
            }
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-emerald-500 focus:outline-none text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">
            Your Location / Town *
          </span>
          <input
            data-testid="mobile-prayer-location"
            type="text"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-emerald-500 focus:outline-none text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">
            Prayer Request *
          </span>
          <textarea
            data-testid="mobile-prayer-request"
            rows={5}
            value={form.request}
            onChange={(e) => setForm({ ...form, request: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-emerald-500 focus:outline-none text-sm resize-none"
            placeholder="Share what's on your heart…"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">
            Phone / WhatsApp (optional)
          </span>
          <input
            data-testid="mobile-prayer-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-emerald-500 focus:outline-none text-sm"
          />
        </label>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            data-testid="mobile-prayer-public"
            checked={form.is_public}
            onChange={(e) =>
              setForm({ ...form, is_public: e.target.checked })
            }
            className="mt-1 accent-emerald-600 w-4 h-4"
          />
          <span>
            My first name and location can be shared on air (untick to stay
            fully private).
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            data-testid="mobile-prayer-consent"
            checked={form.consent}
            onChange={(e) => setForm({ ...form, consent: e.target.checked })}
            className="mt-1 accent-emerald-600 w-4 h-4"
          />
          <span>
            I understand a presenter will read and pray for this request.
          </span>
        </label>

        <button
          data-testid="mobile-prayer-submit-btn"
          onClick={submit}
          disabled={!canSubmit}
          className="w-full py-3 rounded-full font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: 'var(--kioo-primary)' }}
        >
          {phase === 'submitting' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send Prayer Request
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MobilePrayer;
